/**
 * FRIDGE-1187 — the article product floor.
 *
 * The owner: "no blog posts should go without tons of products". The article
 * template (app/blogs/news/[slug]/page.tsx) draws every topic product — the
 * sidebar picks, the inline collection bands, the inline entity cards and the
 * "relevant" rail — from ONE detected collection's in-stock pool. When that
 * pool is thin the page simply rendered fewer products, with nothing behind
 * it. Measured 09/26/2026 across all 16,250 indexable articles: compact-freezer
 * and counter-depth topics had 5 in-stock products each, the lighting topic 15
 * and 7, and water-filter topics 0 (appliance rails never carry filters).
 *
 * The floor: when the topic pool is short, ONE extra read fetches the
 * article's broader appliance family — freezers for a freezer topic,
 * refrigerators for everything else — with the same active in-stock offer gate,
 * and tops the pools up behind the topic products (never ahead of them, never
 * duplicated). Nothing is invented: a failed read leaves the topic pool as it
 * was. The relevant rail's headline widens to the family whenever a family
 * product is on it, so "Top compact freezers" never sits over upright models.
 *
 * Pure: no Supabase, no React, so the full-corpus census runs these same
 * functions in memory (Workspace/fridge-1187-2026-09-26/census.cjs).
 */
import type { RecommendedProduct } from '../recommendations'
import { productMatchesSlug } from '../collection-membership'
import { articleSectionCollections } from './article-section-products'

/** The sidebar/band/entity pool the template requests (`getTrendingInCollection(…, 24)`). */
export const ARTICLE_PICK_POOL_SIZE = 24
/** The "relevant" rail size the template requests (`getTrendingInCollection(…, 12)`). */
export const ARTICLE_RELEVANT_RAIL_SIZE = 12
/**
 * "Many products", in the template's own units: one full relevant rail (12)
 * plus the sidebar's own minimum pick count (3). A healthy article renders at
 * least this many TOPIC products before any site-wide rail is counted.
 */
export const ARTICLE_MIN_TOPIC_PRODUCT_CARDS = ARTICLE_RELEVANT_RAIL_SIZE + 3

const FREEZER_TOPICS = new Set(['freezer', 'chest-freezer', 'upright-freezer', 'compact-freezer', 'deep-freezer'])

export interface ArticleProductFallbackPlan {
  /** Passed to getTrendingInCollection as the collection type. */
  collectionType: 'refrigerator' | 'freezer'
  options: { requireStock: true; category: 'refrigerator' | 'freezer' }
}

/**
 * The verified broader family for a topic. Refrigerators is the site's head
 * family, so it is also the family for topics outside refrigerators/freezers
 * (water filters, wine, beverage, kegerator, ice): the reader of any of those
 * articles owns or is shopping a refrigerator, and the category read can never
 * return a part or a filter.
 */
export function articleProductFallback(detectedCollection: string): ArticleProductFallbackPlan {
  const category = FREEZER_TOPICS.has(detectedCollection) ? 'freezer' : 'refrigerator'
  return { collectionType: category, options: { requireStock: true, category } }
}

/** True when the extra family read is needed — full pools never pay for it. */
export function needsArticleProductFallback(picks: readonly unknown[], relevant: readonly unknown[]): boolean {
  return picks.length < ARTICLE_PICK_POOL_SIZE || relevant.length < ARTICLE_RELEVANT_RAIL_SIZE
}

function topUp(primary: RecommendedProduct[], extra: RecommendedProduct[], size: number): { products: RecommendedProduct[]; added: number } {
  if (primary.length >= size) return { products: primary, added: 0 }
  const ids = new Set(primary.map(p => p.id))
  const urls = new Set(primary.map(p => p.canonical_url).filter(Boolean))
  const out = [...primary]
  let added = 0
  for (const p of extra) {
    if (out.length >= size) break
    if (!p?.id || ids.has(p.id) || (p.canonical_url && urls.has(p.canonical_url))) continue
    ids.add(p.id)
    if (p.canonical_url) urls.add(p.canonical_url)
    out.push(p)
    added++
  }
  return { products: added ? out : primary, added }
}

export function applyArticleProductFloor(input: {
  picks: RecommendedProduct[]
  relevant: RecommendedProduct[]
  fallbackPool: RecommendedProduct[]
  plan: ArticleProductFallbackPlan
  detectedCollection: string
}): { picks: RecommendedProduct[]; relevant: RecommendedProduct[]; fallbackUsed: boolean; relevantCollection: string } {
  const picks = topUp(input.picks, input.fallbackPool, ARTICLE_PICK_POOL_SIZE)
  const relevant = topUp(input.relevant, input.fallbackPool, ARTICLE_RELEVANT_RAIL_SIZE)
  return {
    picks: picks.products,
    relevant: relevant.products,
    fallbackUsed: picks.added > 0 || relevant.added > 0,
    relevantCollection: relevant.added > 0 ? input.plan.collectionType : input.detectedCollection,
  }
}

/**
 * Item 156 remainder: single entity-matched product cards for the band-free
 * sections, drawn from the pick pool after the author's own cards and the band
 * products, image required, one per marked slot. Moved verbatim from page.tsx.
 */
export function selectInlineEntityCards(
  picks: RecommendedProduct[],
  slotCount: number,
  authorProductHrefs: readonly string[],
  bands: ReadonlyArray<{ products: ReadonlyArray<{ canonical_url?: string }> }>,
  contentHtml?: string,
): Array<RecommendedProduct & { slotIndex: number }> {
  if (slotCount === 0 || picks.length === 0) return []
  const used = new Set<string>()
  for (const href of authorProductHrefs) if (href) used.add(href)
  for (const band of bands) for (const p of band.products) if (p.canonical_url) used.add(p.canonical_url)
  const contexts = articleSectionCollections(contentHtml ?? '', 'EPC')
  const cards: Array<RecommendedProduct & { slotIndex: number }> = []
  for (let slotIndex = 0; slotIndex < slotCount; slotIndex++) {
    const slugs = contexts.get(slotIndex) ?? []
    const product = picks.find(p => {
      const image = p.images_supabase?.[0] ?? p.images?.[0]
      return p.canonical_url && !used.has(p.canonical_url) && image && image !== '/placeholder.svg'
        && (!slugs.length || slugs.some(slug => productMatchesSlug(p, slug)))
    })
    if (!product) continue
    used.add(product.canonical_url)
    cards.push({ ...product, slotIndex })
  }
  return cards
}

export interface ArticleSidebarMetrics {
  /** Length of the editorial body with tags stripped. */
  textLength: number
  inlineProducts: number
  comparisonTables: number
  keySpecs: number
  chooseSteps: number
  faqItems: number
  hasRecommendationBox: boolean
}

export function articleSidebarMetrics(article: {
  contentHtml?: string | null
  inlineProducts: readonly unknown[]
  comparisonTables: readonly unknown[]
  keySpecs: readonly unknown[]
  chooseSection?: { steps?: readonly unknown[] } | null
  faqSection?: { items?: readonly unknown[] } | null
}): ArticleSidebarMetrics {
  const html = article.contentHtml || ''
  return {
    textLength: html.replace(/<[^>]+>/g, '').length,
    inlineProducts: article.inlineProducts.length,
    comparisonTables: article.comparisonTables.length,
    keySpecs: article.keySpecs.length,
    chooseSteps: article.chooseSection?.steps?.length || 0,
    faqItems: article.faqSection?.items?.length || 0,
    hasRecommendationBox: html.includes('blog-recommendation-box'),
  }
}

/**
 * Sidebar pick count, moved verbatim from page-client.tsx: estimate the
 * article's rendered height, subtract the always-present tool callout (280px)
 * and fit ~400px picks into the rest, clamped to 3..12.
 */
export function articleSidebarPickTarget(m: ArticleSidebarMetrics): number {
  const estArticleHeight =
    600 +
    m.textLength * 0.25 +
    m.inlineProducts * 320 +
    m.comparisonTables * 300 +
    m.keySpecs * 160 +
    (m.chooseSteps ? 80 + m.chooseSteps * 110 : 0) +
    (m.faqItems ? 80 + m.faqItems * 100 : 0) +
    (m.hasRecommendationBox ? 180 : 0)
  const availableForPicks = Math.max(0, estArticleHeight - 280)
  return Math.max(3, Math.min(12, Math.floor(availableForPicks / 400)))
}

/** Topic-matched product cards on one article: sidebar + bands + entity cards + relevant rail. */
export function countArticleTopicProductCards(parts: { sidebar: number; bands: number; entity: number; relevant: number }): number {
  return parts.sidebar + parts.bands + parts.entity + parts.relevant
}

export function summarizeArticleProductCensus(rows: ReadonlyArray<{ topic: number }>, minimum = ARTICLE_MIN_TOPIC_PRODUCT_CARDS) {
  const sorted = rows.map(r => r.topic).sort((a, b) => a - b)
  const at = (q: number) => sorted[Math.min(sorted.length - 1, Math.floor(q * sorted.length))]
  return {
    articles: sorted.length,
    minimum,
    min: sorted[0],
    p10: at(0.1),
    median: at(0.5),
    belowMinimum: sorted.filter(n => n < minimum).length,
    zero: sorted.filter(n => n === 0).length,
  }
}
